import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Printer,
  Download,
  CreditCard,
  ShieldCheck,
  Layers,
  User,
  CalendarDays,
  Loader2,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  formatPaymentCurrency,
  formatPaymentDate,
  PaymentMethodIcon,
  PUBLIC_PORTAL_BASE_URL,
  RouteLogo,
  RouteOIcon,
  PageLoader,
} from '@repo/ui';

// ── BillDetailPage ────────────────────────────────────────────────────────────

const BillDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { orderNumber, billNumber } = useParams<{ orderNumber: string; billNumber: string }>();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === 'ar';

  const receiptRef = useRef<HTMLDivElement>(null);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const { data: bill, isLoading } = useQuery({
    queryKey: ['student-bill-detail', billNumber],
    queryFn: () => api.get(`/student/payments/bills/${billNumber}`).then((res) => res.data?.data),
    enabled: !!billNumber,
  });

  // ── Shared image capture (scale 2×, oklch-safe via html-to-image) ───────
  // html-to-image leverages native browser rendering via SVG <foreignObject>,
  // which flawlessly supports oklch(), CSS grid, and Tailwind v4.
  const captureImage = async (): Promise<string> => {
    const { toPng } = await import('html-to-image');
    return toPng(receiptRef.current!, {
      pixelRatio: 2,
      backgroundColor: '#ffffff',
      style: { transform: 'scale(1)', transformOrigin: 'top left' },
    });
  };

  // ── Print: png → image → pop-up window → print ──────────────
  // Result: pixel-perfect, no CSS parsing issues, always ONE page.
  const handlePrint = async () => {
    if (!receiptRef.current || isPrintLoading) return;
    setIsPrintLoading(true);
    try {
      const imgData = await captureImage();

      const pw = window.open('', '_blank', 'width=900,height=700');
      if (!pw) return;
      pw.document.write(`
        <!DOCTYPE html>
        <html><head>
          <meta charset="utf-8">
          <title>${bill?.number ?? billNumber}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { background:#fff; }
            img { width:100%; height:auto; display:block; }
            @page { margin:0.5cm; size:A4 portrait; }
            @media print { img { max-width:100%; max-height:100vh; object-fit:contain; } }
          </style>
        </head><body>
          <img src="${imgData}" />
        </body></html>
      `);
      pw.document.close();
      pw.focus();
      setTimeout(() => {
        pw.print();
        pw.close();
      }, 500);
    } catch (err) {
      console.error('[BillDetailPage] Print failed:', err);
    } finally {
      setIsPrintLoading(false);
    }
  };

  // ── Download as PDF — always fits on ONE A4 page ────────────────────────
  const downloadAsPdf = async () => {
    if (!receiptRef.current || isPdfLoading) return;
    setIsPdfLoading(true);
    try {
      const [imgData, jsPDFModule] = await Promise.all([captureImage(), import('jspdf')]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const JsPDF = (jsPDFModule as any).jsPDF ?? (jsPDFModule as any).default ?? jsPDFModule;

      const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;

      // Calculate image dimensions correctly
      const imgProps = pdf.getImageProperties(imgData);
      const aspect = imgProps.width / imgProps.height;

      // Fit within bounds preserving aspect ratio
      let w = maxW;
      let h = maxW / aspect;
      if (h > maxH) {
        h = maxH;
        w = maxH * aspect;
      }

      pdf.addImage(imgData, 'PNG', (pageW - w) / 2, (pageH - h) / 2, w, h);
      pdf.save(`${bill?.number ?? billNumber}.pdf`);
    } catch (err) {
      console.error('[BillDetailPage] PDF generation failed:', err);
    } finally {
      setIsPdfLoading(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────────

  if (isLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  if (!bill) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        {t('common.notFound', { defaultValue: 'Receipt not found.' })}
      </div>
    );
  }

  const backOrderNumber = bill.order?.number ?? orderNumber;
  const amount = bill.amount ?? 0;
  const paymentDate = bill.createdAt ?? bill.date ?? bill.paidAt;

  return (
    <div className="space-y-8 pb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Page Header / Back Action — hidden in print */}
      <div className="flex items-center justify-between print:hidden">
        <button
          onClick={() => navigate(`/payments/${orderNumber}`)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-base font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all group cursor-pointer"
        >
          <ArrowLeft
            className={`h-5 w-5 transition-transform ${isRtl ? 'rotate-180 group-hover:translate-x-1' : 'group-hover:-translate-x-1'}`}
          />
          {t('common.back')}
        </button>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-black uppercase tracking-wider">
          <ShieldCheck className="h-4 w-4" />
          {t('student.receipt.detail.verifiedOperation')}
        </div>
      </div>

      {/* ── Receipt Card (ref used for html2canvas) ── */}
      <div className="max-w-2xl mx-auto">
        <div
          ref={receiptRef}
          className="relative rounded-[40px] border border-slate-200 bg-white shadow-2xl overflow-hidden"
        >
          {/* Top Brand Banner */}
          <div className="bg-[#002c8d] p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex items-center justify-between">
              <div className="space-y-1">
                <div className="h-10">
                  <RouteLogo className="h-full w-auto fill-current" />
                </div>
                <p className="text-white/70 text-sm font-bold tracking-widest uppercase">
                  {t('student.receipt.detail.officialBanner')}
                </p>
              </div>
              <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 p-3">
                <RouteOIcon className="h-full w-full text-white fill-current" />
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 space-y-10">
            {/* Receipt Core Identity */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-100 pb-10">
              <div className="space-y-2">
                <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                  {t('student.receipt.detail.receiptNo')}
                </p>
                <p className="text-4xl font-black font-mono tracking-tight text-slate-900">
                  {bill.number ?? billNumber}
                </p>
              </div>
              <div className="flex items-center gap-3 text-slate-500 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                <CalendarDays className="h-5 w-5" />
                <span className="text-sm font-bold">
                  {formatPaymentDate(paymentDate, lang, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>

            {/* Main Financial Figure */}
            <div className="relative">
              <div className="absolute -inset-4 bg-emerald-50 rounded-[32px]" />
              <div className="relative text-center space-y-2 py-4">
                <p className="text-sm font-black text-slate-400 uppercase tracking-widest">
                  {t('student.receipt.detail.totalPaid')}
                </p>
                <p className="text-6xl font-black text-emerald-600 tabular-nums">
                  {formatPaymentCurrency(amount, lang)}
                </p>
              </div>
            </div>

            {/* Perforated Divider */}
            <div className="relative py-4">
              <div className="border-t-2 border-dashed border-slate-100" />
              <div className="absolute -top-1 -left-12 md:-left-16 h-10 w-10 rounded-full bg-slate-50 border border-slate-100 shadow-inner" />
              <div className="absolute -top-1 -right-12 md:-right-16 h-10 w-10 rounded-full bg-slate-50 border border-slate-100 shadow-inner" />
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {/* Payment Method */}
              <div className="space-y-3">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  {t('student.receipt.detail.paymentMethod')}
                </p>
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
                  <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shadow-sm text-slate-600">
                    <PaymentMethodIcon method={bill.paymentMethod} />
                  </div>
                  <span className="text-base font-black text-slate-900">
                    {t(`common.${bill.paymentMethod?.toLowerCase()}`, {
                      defaultValue: bill.paymentMethod,
                    })}
                  </span>
                </div>
              </div>

              {/* Enrollment Link */}
              <div className="space-y-3">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('student.receipt.detail.linkedEnrollment')}
                </p>
                <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
                  <span className="text-base font-black font-mono tracking-wider text-slate-900">
                    {backOrderNumber}
                  </span>
                </div>
              </div>

              {/* Program Details */}
              {bill.program?.trackName && (
                <div className="space-y-3">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    {t('student.receipt.detail.programTrack')}
                  </p>
                  <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
                    <span className="text-base font-bold text-slate-900">
                      {bill.program.trackName}
                    </span>
                  </div>
                </div>
              )}

              {/* Student Identity */}
              {bill.student?.name && (
                <div className="space-y-3">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {t('student.receipt.verify.student')}
                  </p>
                  <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
                    <span className="text-base font-bold text-slate-900">{bill.student.name}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — Thank you + QR */}
            <div className="pt-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="space-y-2 text-center md:text-start">
                <p className="text-sm font-black text-slate-900">
                  {t('student.receipt.detail.thankYou')}
                </p>
                <p className="text-xs text-slate-400 font-medium max-w-xs">
                  {t('student.receipt.detail.verificationDisclaimer')}
                </p>
              </div>
              <div className="h-28 w-28 p-2 bg-white rounded-3xl border border-slate-100 flex items-center justify-center overflow-hidden shadow-inner">
                {bill.verificationToken ? (
                  <QRCodeCanvas
                    value={`${PUBLIC_PORTAL_BASE_URL}/#/verify/bill/${bill.number ?? billNumber}?sig=${bill.verificationToken}`}
                    size={96}
                    level="M"
                    includeMargin={false}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="text-[10px] text-slate-400 text-center leading-tight">QR N/A</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Buttons — hidden in print ─────────────────────────────── */}
      <div className="print:hidden flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
        {/* Print button */}
        <button
          onClick={handlePrint}
          className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-slate-800 text-white text-base font-black hover:bg-slate-900 transition-all shadow-lg shadow-slate-900/20 active:scale-95"
        >
          <Printer className="h-5 w-5" />
          {t('student.receipt.detail.printAction', { defaultValue: 'طباعة الإيصال' })}
        </button>

        {/* Download PDF button */}
        <button
          onClick={downloadAsPdf}
          disabled={isPdfLoading}
          className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-[#002c8d] text-white text-base font-black hover:opacity-90 transition-all shadow-lg shadow-[#002c8d]/30 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPdfLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Download className="h-5 w-5" />
          )}
          {isPdfLoading
            ? t('student.receipt.detail.pdfGenerating', { defaultValue: 'جاري التحميل...' })
            : t('student.receipt.detail.downloadPdf', { defaultValue: 'تحميل PDF' })}
        </button>
      </div>
    </div>
  );
};

export default BillDetailPage;
